export const accountDeletionTranslations = {
  en: {
    nav: { games: "Games", about: "About", contact: "Contact", browse: "Browse Games" },
    pageTitle: "Account Deletion",
    title: "How to delete your account",
    hero: (gameName) => `A clear guide for permanently deleting your ${gameName} game account and the personal data associated with it.`,
    badge: "Privacy & Safety",
    optionsTitle: "Your data deletion options",
    optionsIntro: (gameName) => `At ${gameName}, we value your privacy and strive to provide a safe and secure gaming environment. We have established a comprehensive user deletion policy to ensure that your personal information is handled responsibly.`,
    optionsBody: "Account deletion means the permanent deletion of your game account and all of the data associated with it. Deletion requests can be initiated through the app or by contacting our support team.",
    contactTitle: "Contact us to delete your user data",
    contactBeforeEmail: "The deletion process can take several days to be completed and, once done, it is irreversible. If you are unable to start the deletion from the app, contact our support team by submitting a request to ",
    contactAfterEmail: ".",
    contactVerification: "In certain cases we reserve the right to ask for additional information to verify ownership of the account before initiating account deletion.",
    contactCancellation: "If at any point you wish to cancel the deletion request, please make sure to let us know within due time. We cannot guarantee that your account has not already been permanently deleted.",
    retentionTitle: "Data retention and deletion",
    retention: [
      "Upon receiving a deletion request, we will take immediate steps to remove your personal data from our systems. This process may take up to 30 days to complete.",
      "During this period, your account will be deactivated, and you will no longer have access to the game or any associated services.",
      "Certain data may be retained for legal or regulatory reasons, such as compliance with financial or tax laws. In such cases, the data will be securely stored and protected in accordance with applicable regulations.",
    ],
    flowLabel: "In-app flow",
    flowTitle: "Deleting your user data from the game",
    flowIntro: (gameName) => `If you wish to delete your account and all associated data through the ${gameName} app, follow these steps.`,
    steps: [
      { title: "Open the game", body: (gameName) => `Open the ${gameName} game on your device.` },
      { title: "Go to settings", body: "Navigate to the settings menu by tapping on the gear icon in the top right corner." },
      { title: "Select Delete Account", body: 'Tap on "Delete Account" and follow the on-screen instructions.' },
      { title: "Confirm deletion", body: "Confirm your decision to delete your account. Note that this action is irreversible." },
    ],
    questionsTitle: "Questions about account deletion?",
    questionsBody: "If you have any questions or concerns regarding our user deletion policy or need assistance with the deletion process, contact our support team.",
    supportLabel: "Contact Support",
    footer: {
      tagline: "WHERE PUZZLES BECOME ADVENTURES",
      blurb: "Lucky Spin Games — a mobile puzzle game studio.",
      games: "GAMES",
      company: "COMPANY",
      getInTouch: "GET IN TOUCH",
      about: "About",
      contact: "Contact",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      copy: (year) => `© ${year} Lucky Spin Games. All rights reserved.`,
    },
  },
  ru: {
    nav: { games: "Игры", about: "О студии", contact: "Контакты", browse: "Наши игры" },
    pageTitle: "Удаление аккаунта",
    title: "Как удалить аккаунт",
    hero: (gameName) => `Подробная инструкция по безвозвратному удалению игрового аккаунта ${gameName} и связанных с ним персональных данных.`,
    badge: "Приватность и безопасность",
    optionsTitle: "Варианты удаления данных",
    optionsIntro: (gameName) => `В ${gameName} мы ценим вашу конфиденциальность и стремимся обеспечить безопасную игровую среду. Мы разработали комплексную политику удаления пользовательских данных, чтобы ответственно обращаться с вашей персональной информацией.`,
    optionsBody: "Удаление аккаунта означает безвозвратное удаление игрового аккаунта и всех связанных с ним данных. Запрос на удаление можно отправить в приложении или через нашу службу поддержки.",
    contactTitle: "Свяжитесь с нами, чтобы удалить данные",
    contactBeforeEmail: "Процесс удаления может занять несколько дней и после завершения необратим. Если вы не можете начать удаление в приложении, отправьте запрос в службу поддержки по адресу ",
    contactAfterEmail: ".",
    contactVerification: "В некоторых случаях мы можем запросить дополнительную информацию, чтобы подтвердить, что аккаунт принадлежит вам, до начала удаления.",
    contactCancellation: "Если вы захотите отменить запрос на удаление, сообщите нам об этом как можно скорее. Мы не можем гарантировать, что аккаунт ещё не был удалён безвозвратно.",
    retentionTitle: "Хранение и удаление данных",
    retention: [
      "После получения запроса на удаление мы незамедлительно начнём удалять ваши персональные данные из наших систем. Этот процесс может занять до 30 дней.",
      "В этот период ваш аккаунт будет деактивирован, а доступ к игре и связанным сервисам будет закрыт.",
      "Некоторые данные могут сохраняться по юридическим или регуляторным причинам, например для соблюдения финансового или налогового законодательства. В таких случаях данные надёжно хранятся и защищаются в соответствии с применимыми требованиями.",
    ],
    flowLabel: "В приложении",
    flowTitle: "Как удалить данные из игры",
    flowIntro: (gameName) => `Чтобы удалить аккаунт и все связанные с ним данные через приложение ${gameName}, выполните следующие шаги.`,
    steps: [
      { title: "Откройте игру", body: (gameName) => `Откройте игру ${gameName} на своём устройстве.` },
      { title: "Перейдите в настройки", body: "Откройте меню настроек, нажав значок шестерёнки в правом верхнем углу." },
      { title: "Выберите «Удалить аккаунт»", body: "Нажмите «Удалить аккаунт» и следуйте инструкциям на экране." },
      { title: "Подтвердите удаление", body: "Подтвердите решение удалить аккаунт. Обратите внимание: это действие необратимо." },
    ],
    questionsTitle: "Есть вопросы об удалении аккаунта?",
    questionsBody: "Если у вас есть вопросы о политике удаления данных или вам нужна помощь с удалением аккаунта, обратитесь в нашу службу поддержки.",
    supportLabel: "Связаться с поддержкой",
    footer: {
      tagline: "ГДЕ ГОЛОВОЛОМКИ СТАНОВЯТСЯ ПРИКЛЮЧЕНИЯМИ",
      blurb: "Lucky Spin Games — студия мобильных игр-головоломок.",
      games: "ИГРЫ",
      company: "КОМПАНИЯ",
      getInTouch: "КОНТАКТЫ",
      about: "О студии",
      contact: "Контакты",
      privacy: "Политика конфиденциальности",
      terms: "Условия использования",
      copy: (year) => `© ${year} Lucky Spin Games. Все права защищены.`,
    },
  },
};
